import { useEffect, useState } from 'react';
import {
  CheckIcon,
  ClipboardDocumentIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';
import DashboardLayout from '../components/DashboardLayout';
import SectionHeader from '../components/partials/SectionHeader';
import StatusBadge from '../components/partials/StatusBadge';
import Pagination from '../components/partials/Pagination';
import AppointmentDetailsModal from '../components/AppointmentDetailsModal';
import { useCheckoutOrders } from '../hooks/useCheckoutOrders';
import { useCalendlyEventDetails } from '../hooks/useCalendlyEventDetails';
import { statusBadge } from '../constants/statusBadge';
import { checkoutBookingUrl } from '../constants/externalLinks';
import { CheckoutOrderListItemDto } from '../types/checkout';

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
}

function CopyButton({ confirmationCode }: { confirmationCode: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(confirmationCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard write failed (permission denied or non-secure context)
    }
  };

  return (
    <div className="group relative">
      <button
        onClick={handleCopy}
        className="flex h-7 w-7 items-center justify-center rounded text-[var(--dash-text-faint)] transition-colors hover:bg-[var(--dash-hover-bg)] hover:text-[var(--dash-text)]"
        aria-label="Copy confirmation number"
      >
        {copied ? (
          <CheckIcon className="h-4 w-4 text-[var(--dash-status-green)]" />
        ) : (
          <ClipboardDocumentIcon className="h-4 w-4" />
        )}
      </button>
      <div className="pointer-events-none absolute bottom-full right-0 mb-1.5 whitespace-nowrap rounded bg-[var(--dash-card-light)] border border-[var(--dash-border-strong)] px-2 py-1 text-xs text-[var(--dash-text)] opacity-0 transition-opacity group-hover:opacity-100">
        {copied ? 'Copied!' : 'Copy confirmation number'}
      </div>
    </div>
  );
}

function OrderActions({
  order,
  onViewAppointment,
}: {
  order: CheckoutOrderListItemDto;
  onViewAppointment: () => void;
}) {
  const isPaid = order.status === 'Paid';

  if (isPaid && order.hasBooking) {
    return (
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={onViewAppointment}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded border border-[var(--dash-gold-border)] px-3 py-2 text-xs font-semibold uppercase tracking-widest text-[var(--dash-gold)] transition-colors hover:border-[var(--dash-gold)] hover:text-[var(--dash-gold-bright)] md:flex-none md:justify-start"
        >
          View Appointment
        </button>
        {order.confirmationCode && (
          <CopyButton confirmationCode={order.confirmationCode} />
        )}
      </div>
    );
  }

  if (isPaid && order.schedulingLink) {
    return (
      <div className="flex items-center justify-end gap-2">
        <a
          href={checkoutBookingUrl(order.id)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded border border-[var(--dash-gold-border)] px-3 py-2 text-xs font-semibold uppercase tracking-widest text-[var(--dash-gold)] transition-colors hover:border-[var(--dash-gold)] hover:text-[var(--dash-gold-bright)] md:flex-none md:justify-start"
        >
          Schedule Appointment
        </a>
        {order.confirmationCode && (
          <CopyButton confirmationCode={order.confirmationCode} />
        )}
      </div>
    );
  }

  if (isPaid && order.confirmationCode) {
    return (
      <div className="flex items-center justify-end">
        <CopyButton confirmationCode={order.confirmationCode} />
      </div>
    );
  }

  if (!isPaid) {
    const badge = statusBadge(order.status);
    return (
      <div className="flex items-center justify-end">
        <StatusBadge variant={badge.variant} label={badge.label} />
      </div>
    );
  }

  return null;
}

export default function OrdersPage() {
  const [pageNumber, setPageNumber] = useState(1);
  const { orders, totalCount, totalPages, loading, error } =
    useCheckoutOrders(pageNumber);

  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const {
    data: eventDetails,
    loading: detailsLoading,
    error: detailsError,
    fetchEventDetails,
    clear: clearEventDetails,
  } = useCalendlyEventDetails();
  useEffect(() => {
    if (selectedOrderId !== null) {
      fetchEventDetails(selectedOrderId);
    }
  }, [selectedOrderId, fetchEventDetails]);

  const handleViewAppointment = (orderId: number) => {
    setSelectedOrderId(orderId);
  };

  const handleCloseModal = () => {
    setSelectedOrderId(null);
    clearEventDetails();
  };

  const handleRescheduleViewClosed = () => {
    if (selectedOrderId !== null) {
      fetchEventDetails(selectedOrderId);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-9 px-4 py-8 md:px-10">
        <section>
          <div className="max-w-7xl">
            <SectionHeader>My Orders</SectionHeader>

            {!loading && !error && totalCount > 0 && (
              <p className="mb-3 text-xs text-[var(--dash-text-faint)]">
                {totalCount} {totalCount === 1 ? 'order' : 'orders'}
              </p>
            )}

            <div className="dash-card overflow-hidden rounded-[10px]">
              {loading && (
                <div className="px-6 py-12 text-sm text-[var(--dash-text-faint)]">
                  Loading…
                </div>
              )}

              {!loading && error && (
                <div className="px-6 py-12 text-sm text-[var(--dash-text-faint)]">
                  Could not load orders. Please try again later.
                </div>
              )}

              {!loading && !error && orders.length === 0 && (
                <div className="px-6 py-12 text-sm text-[var(--dash-text-faint)]">
                  No orders yet.
                </div>
              )}

              {!loading && !error && orders.length > 0 && (
                <>
                  {/* Desktop table — hidden on mobile */}
                  <table className="hidden w-full lg:table">
                    <thead>
                      <tr className="border-b border-[var(--dash-border)]">
                        <th className="px-6 py-4 text-left text-[10.5px] font-semibold uppercase tracking-widest text-[var(--dash-text-faint)]">
                          Service
                        </th>
                        <th className="px-6 py-4 text-left text-[10.5px] font-semibold uppercase tracking-widest text-[var(--dash-text-faint)]">
                          Date
                        </th>
                        <th className="px-6 py-4 text-left text-[10.5px] font-semibold uppercase tracking-widest text-[var(--dash-text-faint)]">
                          Amount
                        </th>
                        <th className="px-6 py-4" aria-hidden="true" />
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order, i) => {
                        const date = formatDate(
                          order.paidAt ?? order.createdAt
                        );
                        return (
                          <tr
                            key={order.id}
                            className={`transition-colors hover:bg-[var(--dash-hover-row)] ${
                              i < orders.length - 1
                                ? 'border-b border-[var(--dash-border)]'
                                : ''
                            }`}
                          >
                            <td className="px-6 py-5">
                              <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg-deep)] text-[var(--dash-text-faint)]">
                                  <DocumentIcon className="h-4 w-4" />
                                </div>
                                <span className="font-serif text-base text-[var(--dash-text)]">
                                  {order.productName ?? '—'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-5 text-sm tabular-nums text-[var(--dash-text-muted)]">
                              {date ?? 'Order'}
                            </td>
                            <td className="px-6 py-5">
                              <span className="text-sm font-semibold tabular-nums text-[var(--dash-text)]">
                                {order.formattedAmount}
                                {order.billingCyclesTotal != null && (
                                  <span className="font-normal text-[var(--dash-text-muted)]">
                                    {' '}
                                    / mo
                                  </span>
                                )}
                              </span>
                              {order.billingCyclesTotal != null && (
                                <p className="mt-0.5 text-xs text-[var(--dash-text-faint)]">
                                  {order.billingCyclesTotal} monthly payments
                                </p>
                              )}
                            </td>
                            <td className="px-6 py-5">
                              <OrderActions
                                order={order}
                                onViewAppointment={() =>
                                  handleViewAppointment(order.id)
                                }
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Mobile card list — hidden on desktop */}
                  <div className="lg:hidden">
                    {orders.map((order) => {
                      const date = formatDate(order.paidAt ?? order.createdAt);
                      const isPaid = order.status === 'Paid';
                      const hasAction =
                        (isPaid &&
                          !!(
                            order.hasBooking ||
                            order.schedulingLink ||
                            order.confirmationCode
                          )) ||
                        !isPaid;
                      return (
                        <div
                          key={order.id}
                          className="dash-row px-[22px] py-[18px]"
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg-deep)] text-[var(--dash-text-faint)]">
                              <DocumentIcon className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="dash-row__title font-serif text-[19px] font-medium leading-snug">
                                {order.productName ?? 'Order'}
                              </p>
                              <p className="dash-row__meta mt-1 text-[14px] font-semibold">
                                {order.formattedAmount}
                                {order.billingCyclesTotal != null && (
                                  <span className="text-[13px] font-normal text-[var(--dash-text-muted)]">
                                    {' '}
                                    / mo · {order.billingCyclesTotal} payments
                                  </span>
                                )}
                              </p>
                              {date && (
                                <p className="dash-row__meta text-[12px]">
                                  {date}
                                </p>
                              )}
                            </div>
                          </div>
                          {hasAction && (
                            <div className="mt-3 pl-12">
                              <OrderActions
                                order={order}
                                onViewAppointment={() =>
                                  handleViewAppointment(order.id)
                                }
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {!loading && !error && totalPages > 1 && (
              <Pagination
                pageNumber={pageNumber}
                totalPages={totalPages}
                onPageChange={setPageNumber}
              />
            )}
          </div>
        </section>
      </div>

      {selectedOrderId !== null && (
        <AppointmentDetailsModal
          details={eventDetails}
          loading={detailsLoading}
          error={detailsError}
          onClose={handleCloseModal}
          onRescheduleViewClosed={handleRescheduleViewClosed}
        />
      )}
    </DashboardLayout>
  );
}
